import { mountNav } from '../components/Header';
import { createShare, uploadMedia } from '../api/client';
import { attachLineNumbers } from '../components/lineNumbers';

export function NewPastePage(): void {
    const app = document.getElementById('app');
    if (!app) return;

    app.innerHTML = `
        <div class="container">
            <header class="header">
                <h1>Mediabin</h1>
                <nav id="nav"></nav>
            </header>
            <main class="main">
                <div class="mode-selector">
                    <h2>New Paste</h2>
                    <div class="mode-tabs">
                        <button class="mode-tab active" data-mode="text">Text</button>
                        <button class="mode-tab" data-mode="text_media">Text + Images</button>
                        <button class="mode-tab" data-mode="media">Images Only</button>
                    </div>
                </div>
                <div class="editor-container" id="editor-container"></div>
                <div class="options-panel">
                    <div class="option-group">
                        <label>Visibility</label>
                        <select id="visibility">
                            <option value="public">Public</option>
                            <option value="unlisted" selected>Unlisted</option>
                            <option value="private" id="private-option" style="display: none;">Private</option>
                            <option value="private_encrypted">Encrypted</option>
                        </select>
                    </div>
                    <div class="option-group">
                        <label>Expiry</label>
                        <select id="expiry">
                            <option value="10m">10 minutes</option>
                            <option value="1h">1 hour</option>
                            <option value="24h">24 hours</option>
                            <option value="7d" selected>7 days</option>
                            <option value="30d">30 days</option>
                            <option value="never">Never</option>
                        </select>
                    </div>
                    <div class="option-group">
                        <label>Syntax Language</label>
                        <select id="syntax-lang">
                            <option value="">None</option>
                            <option value="python">Python</option>
                            <option value="javascript">JavaScript</option>
                            <option value="typescript">TypeScript</option>
                            <option value="html">HTML</option>
                            <option value="css">CSS</option>
                            <option value="sql">SQL</option>
                            <option value="bash">Bash</option>
                            <option value="json">JSON</option>
                            <option value="yaml">YAML</option>
                            <option value="xml">XML</option>
                            <option value="markdown">Markdown</option>
                            <option value="plaintext">Plaintext</option>
                        </select>
                    </div>
                    <div class="option-group checkbox-group">
                        <label>
                            <input type="checkbox" id="burn-after-read">
                            Burn after read
                        </label>
                    </div>
                    <div class="option-group" id="password-group">
                        <label>Password (optional)</label>
                        <input type="password" id="paste-password" placeholder="Enter password">
                    </div>
                </div>
                <div class="actions">
                    <button class="btn btn-primary" id="create-paste-btn">Create Paste</button>
                </div>
                <div class="result" id="result" style="display: none;"></div>
            </main>
        </div>
    `;

    // Setup event listeners
    setupModeTabs();
    setupCreateButton();
    mountNav().then((authed) => {
        const opt = document.getElementById('private-option');
        if (opt) opt.style.display = authed ? '' : 'none';
    });
}

function setupModeTabs(): void {
    const tabs = document.querySelectorAll('.mode-tab');
    tabs.forEach(tab => {
        tab.addEventListener('click', () => {
            tabs.forEach(t => t.classList.remove('active'));
            (tab as HTMLElement).classList.add('active');
            renderEditor((tab as HTMLElement).dataset.mode || 'text');
        });
    });
    renderEditor('text');
}

function renderEditor(mode: string): void {
    const container = document.getElementById('editor-container');
    if (!container) return;
    if (mode === 'media') {
        container.innerHTML = `
            <label class="file-label" for="paste-images">Upload Images</label>
            <input type="file" id="paste-images" accept="image/*" multiple>
        `;
    } else {
        const imageInput = mode === 'text_media'
            ? `<label class="file-label" for="paste-images">Upload Images (optional)</label>
               <input type="file" id="paste-images" accept="image/*" multiple>`
            : '';
        container.innerHTML = `
            <div class="editor-wrap">
                <div class="line-numbers" aria-hidden="true"></div>
                <textarea id="paste-content" placeholder="Paste your text here..."></textarea>
            </div>
            ${imageInput}
        `;
        const textarea = container.querySelector('#paste-content') as HTMLTextAreaElement;
        const gutter = container.querySelector('.line-numbers') as HTMLDivElement;
        attachLineNumbers(textarea, gutter);
    }
}

function setupCreateButton(): void {
    const btn = document.getElementById('create-paste-btn');
    if (btn) {
        btn.addEventListener('click', handleCreatePaste);
    }
}

async function handleCreatePaste(): Promise<void> {
    const btn = document.getElementById('create-paste-btn') as HTMLButtonElement;
    const result = document.getElementById('result');
    if (!btn || !result) return;

    btn.disabled = true;
    btn.textContent = 'Creating...';

    try {
        const mode = (document.querySelector('.mode-tab.active') as HTMLElement)?.dataset.mode || 'text';
        const content = (document.getElementById('paste-content') as HTMLTextAreaElement)?.value || '';
        const visibility = (document.getElementById('visibility') as HTMLSelectElement)?.value || 'unlisted';
        const expires_in = (document.getElementById('expiry') as HTMLSelectElement)?.value || '7d';
        const syntax_lang = (document.getElementById('syntax-lang') as HTMLSelectElement)?.value || undefined;
        const burn_after_read = (document.getElementById('burn-after-read') as HTMLInputElement)?.checked || false;
        const password = (document.getElementById('paste-password') as HTMLInputElement)?.value || undefined;

        const body: Record<string, unknown> = {
            mode,
            content,
            visibility,
            expires_in: expires_in === 'never' ? null : expires_in,
            burn_after_read,
            is_encrypted: visibility === 'private_encrypted',
        };

        if (syntax_lang) body.syntax_lang = syntax_lang;
        if (password) body.password = password;

        const response = await fetch('/api/paste', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
        });

        if (!response.ok) {
            const err = await response.json();
            throw new Error(err.detail || 'Failed to create paste');
        }

        const data = await response.json();

        const imageInput = document.getElementById('paste-images') as HTMLInputElement | null;
        const files = imageInput?.files ? Array.from(imageInput.files) : [];
        const failed: string[] = [];
        let uploaded = 0;
        for (let i = 0; i < files.length; i++) {
            const file = files[i];
            btn.textContent = `Uploading ${i + 1}/${files.length}...`;
            try {
                await uploadMedia(data.id as string, file);
                uploaded++;
            } catch (e) {
                const status = (e as Error & { status?: number }).status;
                const msg = e instanceof Error ? e.message : 'Unknown error';
                failed.push(`${file.name}: ${msg}`);
                if (status === 429) break; // further attempts within the window will fail too
            }
        }

        let shareHtml = '';
        if (visibility === 'private') {
            try {
                const { share_url } = await createShare(data.id);
                shareHtml = `
                    <div class="url-display">
                        <input type="text" value="${share_url}" readonly id="share-url">
                        <button class="btn btn-secondary" id="copy-share-btn">Copy share link</button>
                    </div>
                `;
            } catch {
                shareHtml = `
                    <p class="error-msg">Could not generate a share link. Create one from <a href="/me">My Pastes</a>.</p>
                `;
            }
        }

        result.style.display = 'block';
        const uploadNote = failed.length === 0
            ? ''
            : `<div class="error"><p>Uploaded ${uploaded}/${files.length} images. Failed:</p><ul>${failed.map(f => `<li>${f}</li>`).join('')}</ul><p>Wait a minute and retry the failed files from Edit, or create another paste.</p></div>`;
        result.innerHTML = `
            <div class="success">
                <p>Paste created successfully!${files.length ? ` (${uploaded}/${files.length} images uploaded)` : ''}</p>
                ${uploadNote}
                <div class="url-display">
                    <input type="text" value="${data.url}" readonly id="paste-url">
                    <button class="btn btn-secondary" id="copy-url-btn">Copy</button>
                </div>
                ${shareHtml}
            </div>
        `;

        const copyBtn = document.getElementById('copy-url-btn');
        if (copyBtn) {
            copyBtn.addEventListener('click', () => {
                const urlInput = document.getElementById('paste-url') as HTMLInputElement;
                urlInput.select();
                navigator.clipboard.writeText(urlInput.value);
                copyBtn.textContent = 'Copied!';
                setTimeout(() => { copyBtn.textContent = 'Copy'; }, 2000);
            });
        }

        const copyShareBtn = document.getElementById('copy-share-btn');
        if (copyShareBtn) {
            copyShareBtn.addEventListener('click', () => {
                const urlInput = document.getElementById('share-url') as HTMLInputElement;
                urlInput.select();
                navigator.clipboard.writeText(urlInput.value);
                copyShareBtn.textContent = 'Copied!';
                setTimeout(() => { copyShareBtn.textContent = 'Copy share link'; }, 2000);
            });
        }
    } catch (error) {
        result.style.display = 'block';
        result.innerHTML = `<div class="error"><p>Error: ${error instanceof Error ? error.message : 'Unknown error'}</p></div>`;
    } finally {
        btn.disabled = false;
        btn.textContent = 'Create Paste';
    }
}
